import { t } from '../i18n.js';

class UpdatePrompt extends HTMLElement {
 
    // ── A. Shadow DOM setup ──────────────────────────────────────────────
    constructor() {
      super();
      this._shadow   = this.attachShadow({ mode: "open" });
      this._version  = "";
      this._mode     = "optional"; // "optional" | "mandatory"
      this._visible  = false;
      this._render();
    }
 
    // ── B. Lifecycle ─────────────────────────────────────────────────────
    connectedCallback() {
      this._bindEvents();
    }
 
    // ── C. Observed attributes ────────────────────────────────────────────
    static get observedAttributes() {
      return ["version", "mode", "open"];
    }
 
    attributeChangedCallback(name, _, newVal) {
      if (name === "version") { this._version = newVal || ""; this._updateContent(); }
      if (name === "mode")    { this._mode    = newVal === "mandatory" ? "mandatory" : "optional"; this._updateContent(); }
      if (name === "open")    { newVal !== null ? this.show() : this.hide(); }
    }
 
    // ── D. JS property setters ────────────────────────────────────────────
    set version(v) {
      this._version = String(v || "");
      this._updateContent();
    }
 
    get version() { return this._version; }
 
    set mode(m) {
      this._mode = m === "mandatory" ? "mandatory" : "optional";
      this._updateContent();
    }
 
    get mode() { return this._mode; }
 
    // ── E. Public API ─────────────────────────────────────────────────────
    show() {
      this._visible = true;
      const overlay = this._shadow.getElementById("overlay");
      const sheet   = this._shadow.getElementById("sheet");
      if (!overlay) return;
 
      // Block everything outside this component
      this._setPageInert(true);
 
      overlay.classList.add("visible");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          sheet.classList.add("in");
          // Move focus to the primary button after animation settles
          setTimeout(() => {
            const btn = this._shadow.getElementById("btn-confirm");
            btn?.focus();
          }, 340);
        });
      });
    }
 
    hide() {
      this._visible = false;
      const overlay = this._shadow.getElementById("overlay");
      const sheet   = this._shadow.getElementById("sheet");
      if (!overlay) return;
 
      // Restore interaction to the rest of the page
      this._setPageInert(false);
 
      sheet.classList.remove("in");
      sheet.classList.add("out");
      setTimeout(() => {
        overlay.classList.remove("visible");
        sheet.classList.remove("out");
      }, 320);
    }
 
    // ── E2. Mark every sibling element inert while open ───────────────────
    // This blocks clicks, scroll, and focus outside the component.
    _setPageInert(inert) {
      const parent = this.parentNode || document.body;
      Array.from(parent.children).forEach(el => {
        if (el === this) return;
        if (inert) {
          // Record whether it was already inert BEFORE we touch it
          el.dataset._wasInert = el.hasAttribute("inert") ? "1" : "0";
          el.setAttribute("inert", "");
        } else {
          // Only remove inert if WE added it — leave already-inert nodes alone
          if (el.dataset._wasInert === "0") el.removeAttribute("inert");
          delete el.dataset._wasInert;
        }
      });
      // Block body scroll and prevent Escape from bubbling out
      if (inert) {
        document.body.style.overflow = "hidden";
        this._escapeGuard = (e) => {
          if (e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); }
        };
        document.addEventListener("keydown", this._escapeGuard, true);
      } else {
        document.body.style.overflow = "";
        if (this._escapeGuard) {
          document.removeEventListener("keydown", this._escapeGuard, true);
          this._escapeGuard = null;
        }
      }
    }
 
    // ── F1. Translate with placeholder support ──────────────────────────
    _tt(key, params = {}) {
      let str = t(key);
      for (const [k, v] of Object.entries(params)) {
        str = str.replace(`{${k}}`, v);
      }
      return str;
    }

    // ── F2. Build the mode-specific content block ──────────────────────────
    _contentHTML() {
      const ver = this._version ? `${this._version}` : t("update-new-version");
      const isMandatory = this._mode === "mandatory";

      if (isMandatory) {
        return `
          <div class="header">
            <div class="icon-wrap mandatory">
              <span class="icon">
                <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 4L4 26h24L16 4z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" fill="none"/>
                  <path d="M16 13v6M16 22v2" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
                </svg>
              </span>
            </div>
            <span class="title">${t("update-required-title")}</span>
          </div>
         
          <p class="body">
            ${this._tt("update-required-body", { ver })}
          </p>
          <div class="actions single">
            <button class="btn btn-primary" id="btn-confirm">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 2v8M5 7l3 3 3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M3 13h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              ${t("update-required-btn")}
            </button>
          </div>`;
      } else {
        return `
          <div class="header">
            <div class="icon-wrap optional">
              <span class="icon">
                <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="16" r="12" stroke="currentColor" stroke-width="2"/>
                  <path d="M16 9v7M16 20v2" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
                </svg>
              </span>
            </div>
            <span class="title">${t("update-available-title")}</span>
          </div>
          <p class="body">
            ${this._tt("update-available-body", { ver })}
          </p>
          <div class="actions double">
            <button class="btn btn-secondary" id="btn-defer">
              ${t("update-btn-later")}
            </button>
            <button class="btn btn-primary" id="btn-confirm">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 2v8M5 7l3 3 3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M3 13h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              ${t("update-btn-now")}
            </button>
          </div>`;
      }
    }
 
    // ── G. Patch only the content node (no full re-render) ────────────────
    _updateContent() {
      const content = this._shadow.getElementById("content");
      if (!content) return;
      content.innerHTML = this._contentHTML();
      // Re-bind the new buttons
      this._bindButtons();
    }
 
    // ── H. Bind overlay + buttons ─────────────────────────────────────────
    _bindEvents() {
      // Swallow all clicks on the backdrop — nothing dismisses without a button
      this._shadow.getElementById("overlay")
        ?.addEventListener("click", e => { e.stopPropagation(); });
 
      // Focus trap: keep Tab/Shift+Tab cycling inside the sheet
      this._shadow.getElementById("sheet")
        ?.addEventListener("keydown", e => {
          if (e.key !== "Tab") return;
          const focusable = Array.from(
            this._shadow.querySelectorAll("button:not([disabled])")
          );
          if (!focusable.length) return;
          const first = focusable[0];
          const last  = focusable[focusable.length - 1];
          if (e.shiftKey) {
            if (this._shadow.activeElement === first) {
              e.preventDefault();
              last.focus();
            }
          } else {
            if (this._shadow.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        });
 
      this._bindButtons();
    }
 
    _bindButtons() {
      this._shadow.getElementById("btn-confirm")
        ?.addEventListener("click", () => this._handleConfirm());
 
      this._shadow.getElementById("btn-defer")
        ?.addEventListener("click", () => this._handleDefer());
    }
 
    // ── I. Action handlers ────────────────────────────────────────────────
    _handleConfirm() {
      const eventName = this._mode === "mandatory" ? "update-forced" : "update-accepted";
      this._dispatch(eventName, { version: this._version, mode: this._mode });
      this.hide();
    }
 
    _handleDefer() {
      if (this._mode === "mandatory") return; // can't defer mandatory
      this._dispatch("update-deferred", { version: this._version, mode: this._mode });
      this.hide();
    }
 
    // ── J. Fire a custom event ────────────────────────────────────────────
    _dispatch(name, detail) {
      this.dispatchEvent(new CustomEvent(name, {
        detail, bubbles: true, composed: true
      }));
    }
 
    // ── K. Full render ────────────────────────────────────────────────────
    _render() {
      this._shadow.innerHTML = `
        <style>
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
 
          :host {
            display: block;
            font-family: system-ui, sans-serif;
          }
 
          /* ── Overlay backdrop ── */
          .overlay {
            display: none;
            position: fixed;
            inset: 0;
            z-index: 9000;
            background: rgba(0, 0, 0, 0.45);
            backdrop-filter: blur(3px);
            -webkit-backdrop-filter: blur(3px);
            align-items: flex-end;
            justify-content: center;
            padding-bottom: env(safe-area-inset-bottom, 0px);
            /* Backdrop itself does not receive pointer events — only the sheet does */
            pointer-events: none;
          }
 
          .overlay.visible { display: flex; }
 
          /* ── Bottom sheet ── */
          .sheet {
            background: #fff;
            border-radius: 20px 20px 0 0;
            width: 100%;
            max-width: 90dvw;
            padding: 0 0 calc(16px + env(safe-area-inset-bottom, 0px));
            transform: translateY(100%);
            transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1);
            will-change: transform;
            overflow: hidden;
            /* Sheet re-enables pointer events */
            pointer-events: all;
          }
 
          .sheet.in  { transform: translateY(0); }
          .sheet.out { transform: translateY(100%); }
 
          /* Handle bar */
          .sheet::before {
            content: "";
            display: block;
            width: 36px;
            height: 4px;
            border-radius: 2px;
            background: #e0dfd7;
            margin: 12px auto 0;
          }
 
          /* ── Content ── */
          #content {
            padding: 20px 24px 8px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 14px;
          }

          .header {
            display: flex;
            flex-direction: rows;
            gap: 10px;
          }
 
          /* ── Icon ── */
          .icon-wrap {
            width: 52px;
            height: 52px;
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            flex-direction: column;
          }
 
          .icon-wrap.optional {
            background: #4E3930;
            color: #ffffff;
          }
 
          .icon-wrap.mandatory {
            background: #4E3930;
            color: #ffffff;
          }
          
          .icon {
            line-height: 0.8;
          }
 
          /* ── Title ── */
          .title {
            font-size: 19px;
            font-weight: 600;
            color: #1a1a1a;
            letter-spacing: -0.02em;
            line-height: 1.2;
            align-self: center;
          }
 
          /* ── Body text ── */
          .body {
            font-size: 14px;
            color: #666;
            line-height: 1.6;
          }
 
          .body strong {
            color: #1a1a1a;
            font-weight: 500;
          }
 
          /* ── Version pill ── */
          .version-pill {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            font-size: 11px;
            font-weight: 500;
            padding: 3px 9px;
            border-radius: 99px;
            background: #f5f5f3;
            color: #888;
            border: 0.5px solid #e0dfd7;
            margin-bottom: 2px;
          }
 
          /* ── Actions ── */
          .actions {
            width: 100%;
            display: flex;
            gap: 10px;
            padding-top: 6px;
          }
 
          .actions.single { flex-direction: column; }
          .actions.double { flex-direction: row; }
 
          /* ── Buttons ── */
          .btn {
            flex: 1;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            padding: 13px 16px;
            font: inherit;
            font-size: 15px;
            font-weight: 500;
            border-radius: 8px;
            border: 0.5px solid #e0dfd7;
            cursor: pointer;
            transition: opacity 0.15s, transform 0.1s;
            touch-action: manipulation;
            -webkit-tap-highlight-color: transparent;
          }
 
          .btn:active { transform: scale(0.97); }
 
          .btn-primary {
            background: #4E3930;
            color: #fff;
          }
 
          .btn-primary:hover { opacity: 0.85; }
 
          .btn-secondary {
            background: #f5f5f3;
            color: #555;
            border: 0.5px solid #d0cfc7;
          }
 
          .btn-secondary:hover { background: #ededea; }
 
          /* ── Divider ── */
          .divider {
            width: 100%;
            height: 0.5px;
            background: #e0dfd7;
          }
 
          /* ── Dark mode ── */
          /*
          @media (prefers-color-scheme: dark) {
            .sheet           { background: #1e1e1c; }
            .sheet::before   { background: #333330; }
            .title           { color: #f0ede8; }
            .body            { color: #888; }
            .body strong     { color: #f0ede8; }
            .version-pill    { background: #2a2a28; border-color: #333330; color: #666; }
            .btn-secondary   { background: #2a2a28; border-color: #333330; color: #888; }
            .btn-secondary:hover { background: #333330; }
            .btn-primary     { background: #f0ede8; color: #1a1a1a; }
            .divider         { background: #333330; }
            .icon-wrap.optional  { background: #0c2a40; color: #7ab9dc; }
            .icon-wrap.mandatory { background: #3a2000; color: #f0a030; }
          }
          */
 
          /* ── Responsive: treat like a modal if viewport is wide ── */
          @media (min-width: 600px) {
            .overlay {
              align-items: center;
            }
 
            .sheet {
              border-radius: 20px;
              max-width: 400px;
              margin: 0 16px;
              transform: scale(0.94) translateY(8px);
              opacity: 0;
              transition:
                transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1),
                opacity   0.22s ease;
              pointer-events: all;
            }
 
            .sheet.in {
              transform: scale(1) translateY(0);
              opacity: 1;
            }
 
            .sheet.out {
              transform: scale(0.94) translateY(8px);
              opacity: 0;
            }
 
            .sheet::before { display: none; }
 
            #content { padding-top: 28px; }
          }
        </style>
 
        <div class="overlay" id="overlay" role="dialog" aria-modal="true"
             aria-labelledby="update-title">
          <div class="sheet" id="sheet">
            <div id="content">
              ${this._contentHTML()}
            </div>
          </div>
        </div>
      `;
    }
  }

export { UpdatePrompt }